/**
 * React Query hooks for Leads companies.
 *
 * Replaces the prior useState/useEffect implementation. Server state
 * caches in `@tanstack/react-query` so switching tabs no longer
 * refetches and mutations invalidate cleanly. Query keys come from
 * `lib/query-keys.ts` (`queryKeys.leads.companies.*`).
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { LeadsCreateCompanyInput, LeadsListCompaniesResult } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

interface UseCompaniesOptions {
  search?: string;
  industry?: string;
  status?: 'active' | 'archived' | 'deleted';
  sortBy?: string;
  sortDir?: string;
  limit?: number;
  offset?: number;
}

function buildParams(options: UseCompaniesOptions): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (options.search) params.search = options.search;
  if (options.industry) params.industry = options.industry;
  if (options.status) params.status = options.status;
  if (options.sortBy) params.sortBy = options.sortBy;
  if (options.sortDir) params.sortDir = options.sortDir;
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.offset !== undefined) params.offset = options.offset;
  return params;
}

export function useCompanies(options: UseCompaniesOptions = {}) {
  const queryClient = useQueryClient();
  const filters = buildParams(options);

  const query = useQuery<LeadsListCompaniesResult>({
    queryKey: queryKeys.leads.companies.list(filters),
    queryFn: () => api.listLeadsCompanies(filters),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.leads.companies.all() });

  const createMutation = useMutation({
    mutationFn: (input: LeadsCreateCompanyInput) => api.createLeadsCompany(input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create company', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<LeadsCreateCompanyInput> }) =>
      api.updateLeadsCompany(id, input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to update company', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLeadsCompany(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to delete company', variant: 'destructive' }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.archiveLeadsCompany(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to archive company', variant: 'destructive' }),
  });

  // Surface the query error so callers see the toast even when their
  // callbacks don't trigger (e.g. on initial mount).
  useEffect(() => {
    if (query.isError && !query.isFetching) {
      toast({ title: 'Error', description: 'Failed to load companies', variant: 'destructive' });
    }
  }, [query.isError, query.isFetching]);

  return {
    companies: query.data?.companies ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createCompany: (input: LeadsCreateCompanyInput) => createMutation.mutateAsync(input),
    updateCompany: (id: string, input: Partial<LeadsCreateCompanyInput>) =>
      updateMutation.mutateAsync({ id, input }),
    deleteCompany: (id: string) => deleteMutation.mutateAsync(id),
    archiveCompany: (id: string) => archiveMutation.mutateAsync(id),
  };
}
